import { Component, computed, inject, input, OnInit } from '@angular/core';
import {
  AllExports,
  RingCentralExportConnectionDetails,
  RingCentralExportStatus,
} from '../../../shared/models/generated/ziroModels';
import { MigrationsCiscoResource } from '../../services/cisco/migrations-cisco.resource';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { from, Observable } from 'rxjs';
import { FileUploadBoxConfig } from '../../../forms/fields/file-upload-box/file-upload-box.component';
import { TarReader } from '@gera2ld/tarjs';
import { catchError, delayWhen, map, switchMap } from 'rxjs/operators';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { CdrValidationService } from '../../../shared/services/cdr-validation.service';
import { FileUploadService } from '../../../shared/services/file-upload.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { TranslateService } from '@ngx-translate/core';
import { ZiroRadioConfig } from '../../../forms/fields/radio/ziro-radio.component';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import { MigrationsRingCentralResource } from '../../services/ring-central/migrations-ring-central.resource';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { MigrationsRingCentralPollingModalComponent } from './migrations-ring-central-polling-modal.component';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';

interface MigrationsCreateFormData {
  name: string;
  pbxType: AssessmentPbxType;
  ciscoTar: File[];
  ciscoCdr: File[];
  ciscoUnity: File[];
  ciscoCer: File[];
  ringCentralClientId: string;
  ringCentralClientSecret: string;
  ringCentralJwtToken: string;
}

enum AssessmentPbxType {
  CISCO = 'CISCO',
  RING_CENTRAL = 'RING_CENTRAL',
}

@Component({
  selector: 'ziro-migrations-create-form',
  templateUrl: 'migrations-create-form.component.html',
  styleUrls: ['migrations-create-form.component.scss'],
  providers: [CdrValidationService, FileUploadService],
  standalone: false,
})
export class MigrationsCreateFormComponent
  extends ZiroFormAbstractDirective<MigrationsCreateFormData>
  implements OnInit
{
  readonly allAssessments = input.required<AllExports>();

  readonly allAssessmentNames = computed(() => {
    const assessments: AllExports[keyof AllExports][] = Object.values(this.allAssessments());
    return assessments.flat().map((assessment) => assessment.name);
  });

  private static REQUIRED_TAR_CSVS = [
    'linegroup.csv',
    'huntpilot.csv',
    'huntlist.csv',
    'directorynumber.csv',
    'phone.csv',
    'deviceprofile.csv',
    'enduser.csv',
    'ldapdir.csv',
  ];

  private _migrationsAssessmentContext = inject(MigrationsAssessmentsContext);
  private _migrationsCiscoResource = inject(MigrationsCiscoResource);
  private _migrationsRingCentralResource = inject(MigrationsRingCentralResource);
  private _bottomNavService = inject(BottomNavService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);
  private _cdrValidationService = inject(CdrValidationService);
  private _fileUploadService = inject(FileUploadService);
  private _ziroModalService = inject(ZiroModalService);

  private _tarMissingCsvs: string[] = [];

  private _unityMissingFolders: string[] = [];
  private _unityMissingListJsons: string[] = [];

  private _cerValidationMessage = '';
  private _cerErlFiles: File[] = [];
  private _cerSubnetFiles: File[] = [];
  private _cerAccessPointFiles: File[] = [];
  private _cerSwitchFiles: File[] = [];
  private _cerSwitchPortFiles: File[] = [];

  formConfig: ZiroFormConfig;
  AssessmentPbxType = AssessmentPbxType;

  ngOnInit(): void {
    this._initBottomNav();
    this._initForm();

    this.ziroFormsUpdate$.subscribe((update) => {
      if (this.isFormSubmitted) {
        this._setBottomNavErrorState(update.valid === ZiroFormsValidationState.INVALID);
      }
      if (update.new.pbxType && update.new.pbxType !== update.old?.pbxType) {
        this._initBottomNav(update.new.pbxType);
      }
    });
    this._validateAndSubmitSource.subscribe(() => {
      this._setBottomNavErrorState(!this.isFormValid());
    });
  }

  protected submit(): Observable<void> {
    const tenantId = this._route.snapshot.params.tenantId;
    this._setButtonPending(true);
    return this._submitAssessment(tenantId);
  }

  private _submitAssessment(tenantId: string): Observable<void> {
    switch (this.entity.pbxType) {
      case AssessmentPbxType.CISCO:
        return this._submitCiscoAssessment(tenantId);
      case AssessmentPbxType.RING_CENTRAL:
        return this._submitRingCentralAssessment(tenantId);
    }
  }

  private _submitCiscoAssessment(tenantId: string): Observable<void> {
    const fileUploadObservable = this._migrationsCiscoResource.createAssessment(
      tenantId,
      this.entity.name,
      this.entity.ciscoTar && this.entity.ciscoTar[0],
      this.entity.ciscoCdr && this.entity.ciscoCdr[0],
      this.entity.ciscoUnity && this.entity.ciscoUnity[0],
      this._cerErlFiles[0],
      this._cerSubnetFiles[0],
      this._cerAccessPointFiles[0],
      this._cerSwitchFiles[0],
      this._cerSwitchPortFiles[0],
    );
    return this._fileUploadService.upload(fileUploadObservable, 'tkey;migrations.create.cisco.submit.progress').pipe(
      delayWhen(() => this._migrationsAssessmentContext.refresh()),
      map((ciscoPbxExportRef) => {
        this._ziroFormStateService.setIsFormDirty(false);
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.MIGRATIONS_MODULE,
          'tkey;migrations.create.success.title',
          'tkey;migrations.create.success.message',
          { name: this.entity.name },
        );
        this._router.navigate(['..', 'cisco', ciscoPbxExportRef.id], { relativeTo: this._route });
      }),
    );
  }

  private _submitRingCentralAssessment(tenantId: string): Observable<void> {
    const requestBody: RingCentralExportConnectionDetails = {
      name: this.entity.name,
      clientId: this.entity.ringCentralClientId,
      clientSecret: this.entity.ringCentralClientSecret,
      jwtToken: this.entity.ringCentralJwtToken,
    };
    return this._migrationsRingCentralResource.validate(tenantId, requestBody).pipe(
      switchMap(() => this._migrationsRingCentralResource.post(tenantId, requestBody)),
      delayWhen(() => this._migrationsAssessmentContext.refresh()),
      switchMap((ref) => {
        return this._ziroModalService
          .openCustomModal(
            MigrationsRingCentralPollingModalComponent,
            { tenantId: String(tenantId), exportId: ref.id },
            { size: 'md' },
          )
          .pipe(map((status: RingCentralExportStatus) => ({ status, exportId: ref.id })));
      }),
      map(({ status, exportId }) => {
        if (status === RingCentralExportStatus.COMPLETED) {
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.MIGRATIONS_MODULE,
            'tkey;migrations.create.ring_central.polling_modal.success.title',
            'tkey;migrations.create.ring_central.polling_modal.success.message',
          );
        }
        if (status === RingCentralExportStatus.COMPLETED || status === RingCentralExportStatus.CREATING) {
          this._ziroFormStateService.setIsFormDirty(false);
          this._router.navigate(['..', 'ring-central', exportId], { relativeTo: this._route });
        } else {
          this._setButtonPending(false);
        }
      }),
      catchError((err) => {
        this._setButtonPending(false);
        throw err;
      }),
    );
  }

  private _isUnityZipValid(file: File): Observable<boolean> {
    return new Observable((subscriber) => {
      file.text().then((contents) => {
        this._unityMissingFolders = [];
        this._unityMissingListJsons = [];

        this._checkUnityZipContentsForFolder(contents, 'callhandlers');
        this._checkUnityZipContentsForFolder(contents, 'directoryhandlers');
        this._checkUnityZipContentsForFolder(contents, 'distributionlists');
        this._checkUnityZipContentsForFolder(contents, 'interviewhandlers');
        this._checkUnityZipContentsForFolder(contents, 'partitions');
        this._checkUnityZipContentsForFolder(contents, 'routingrules');
        this._checkUnityZipContentsForFolder(contents, 'schedules');
        this._checkUnityZipContentsForFolder(contents, 'schedulesets');
        this._checkUnityZipContentsForFolder(contents, 'users');

        subscriber.next(!this._unityMissingFolders.length && !this._unityMissingListJsons.length);
        subscriber.complete();
      });
    });
  }

  private _checkUnityZipContentsForFolder(contents: string, folder: string): void {
    if (!contents.includes(`${folder}/`)) {
      this._unityMissingFolders.push(folder);
    } else if (!contents.includes(`${folder}/list.json`)) {
      this._unityMissingListJsons.push(folder);
    }
  }

  private _isCerFileArrayValid(files: File[]): Observable<boolean> {
    return new Observable((subscriber) => {
      this._cerErlFiles = [];
      this._cerSubnetFiles = [];
      this._cerAccessPointFiles = [];
      this._cerSwitchFiles = [];
      this._cerSwitchPortFiles = [];
      const invalidFiles: File[] = [];

      const fileReads = files.map((file) => {
        return file.text().then((contents) => {
          if (!contents) {
            invalidFiles.push(file);
          } else if (contents.startsWith('ERLName,')) {
            this._cerErlFiles.push(file);
          } else if (contents.startsWith('SubnetID,')) {
            this._cerSubnetFiles.push(file);
          } else if (contents.startsWith('Access Point Name,')) {
            this._cerAccessPointFiles.push(file);
          } else if (contents.startsWith('IP/HostName,')) {
            this._cerSwitchFiles.push(file);
          } else if (contents.startsWith('ERL Name,')) {
            this._cerSwitchPortFiles.push(file);
          } else {
            invalidFiles.push(file);
          }
        });
      });

      Promise.all(fileReads).then(() => {
        this._cerValidationMessage = '';

        if (invalidFiles.length > 0) {
          this._cerValidationMessage = this._translateService.instant(
            'tkey;migrations.create.cisco.file_upload.cer_configuration.format',
            { fileNames: invalidFiles.map((file) => file.name).join(', ') },
          );
        } else if (
          !this._cerErlFiles.length ||
          this._cerSubnetFiles.length +
            this._cerAccessPointFiles.length +
            this._cerSwitchFiles.length +
            this._cerSwitchPortFiles.length ===
            0
        ) {
          // ERL file and at least one other file is required
          this._cerValidationMessage = this._translateService.instant(
            'tkey;migrations.create.cisco.file_upload.cer_configuration.missing_required',
          );
        } else if (this._cerErlFiles.length > 1) {
          this._cerValidationMessage = this._translateService.instant(
            'tkey;migrations.create.cisco.file_upload.cer_configuration.multiple_of_type',
            { fileType: 'ERL', fileNames: this._cerErlFiles.map((file) => file.name).join(', ') },
          );
        } else if (this._cerSubnetFiles.length > 1) {
          this._cerValidationMessage = this._translateService.instant(
            'tkey;migrations.create.cisco.file_upload.cer_configuration.multiple_of_type',
            { fileType: 'Subnet', fileNames: this._cerSubnetFiles.map((file) => file.name).join(', ') },
          );
        } else if (this._cerAccessPointFiles.length > 1) {
          this._cerValidationMessage = this._translateService.instant(
            'tkey;migrations.create.cisco.file_upload.cer_configuration.multiple_of_type',
            { fileType: 'Access Point', fileNames: this._cerAccessPointFiles.map((file) => file.name).join(', ') },
          );
        } else if (this._cerSwitchFiles.length > 1) {
          this._cerValidationMessage = this._translateService.instant(
            'tkey;migrations.create.cisco.file_upload.cer_configuration.multiple_of_type',
            { fileType: 'Switch', fileNames: this._cerSwitchFiles.map((file) => file.name).join(', ') },
          );
        } else if (this._cerSwitchPortFiles.length > 1) {
          this._cerValidationMessage = this._translateService.instant(
            'tkey;migrations.create.cisco.file_upload.cer_configuration.multiple_of_type',
            { fileType: 'Switch Port', fileNames: this._cerSwitchPortFiles.map((file) => file.name).join(', ') },
          );
        }
        subscriber.next(!this._cerValidationMessage);
        subscriber.complete();
      });
    });
  }

  private _initBottomNav(pbxType?: AssessmentPbxType): void {
    const buttons: BottomNavButton[] = [
      {
        id: 'migrations-create-cancel-button',
        label: 'tkey;global.button.cancel.text',
        buttonClass: ButtonStyles.DEFAULT,
        dataAutomation: 'migrations-cisco-cancel-button',
        cb: () => {
          this._router.navigate(['..'], { relativeTo: this._route });
        },
      },
    ];
    switch (pbxType) {
      case AssessmentPbxType.CISCO:
        buttons.push({
          id: 'migrations-create-submit-button',
          label: 'tkey;migrations.create.cisco.submit',
          buttonClass: ButtonStyles.PRIMARY,
          icon: ZiroIcons.SAVE_SUCCESS,
          type: ButtonTypes.SUBMIT,
          dataAutomation: 'migrations-create-button',
          submitSubject: this._validateAndSubmitSource,
        });
        break;
      case AssessmentPbxType.RING_CENTRAL:
        buttons.push({
          id: 'migrations-create-submit-button',
          label: 'tkey;migrations.create.ring_central.fetch',
          buttonClass: ButtonStyles.PRIMARY,
          icon: ZiroIcons.SAVE_SUCCESS,
          type: ButtonTypes.SUBMIT,
          dataAutomation: 'migrations-create-button',
          submitSubject: this._validateAndSubmitSource,
        });
        break;
    }
    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(buttons));
  }

  private _setButtonPending(isPending: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'migrations-create-submit-button',
        state: {
          pending: isPending,
          buttonDisableState: { disabled: isPending, tooltipKey: '' },
        },
      }),
    );
  }

  private _setBottomNavErrorState(hasError: boolean): void {
    this._bottomNavService.dispatch(new BottomNavUpdateState({ hasValidationError: hasError }));
  }

  private _initForm(): void {
    this.formConfig = {
      fields: {
        name: {
          label: 'tkey;migrations.create.assessment_name',
          dataAutomation: 'migrations-create-name-input',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
            placeholder: 'tkey;migrations.create.assessment_name.placeholder',
          }),
          required: true,
          validation: [
            {
              validator: (val: string) => {
                return val &&
                  this.allAssessmentNames().some((assessmentName) => val.toLowerCase() === assessmentName.toLowerCase())
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;migrations.create.assessment_name.unique',
            },
          ],
        },
        pbxType: {
          label: 'tkey;migrations.create.pbx_type',
          dataAutomation: 'migrations-create-pbx-type-radio',
          componentConfig: new ZiroRadioConfig<AssessmentPbxType>({
            buttons: [
              { label: 'tkey;migrations.create.pbx_type.cisco', value: AssessmentPbxType.CISCO },
              { label: 'tkey;migrations.create.pbx_type.ring_central', value: AssessmentPbxType.RING_CENTRAL },
            ],
            inline: true,
          }),
          required: true,
        },
        ciscoTar: {
          dataAutomation: 'migrations-create-cisco-tar-input',
          componentConfig: new FileUploadBoxConfig({
            maxSize: '500MB',
            defaultFileTypes: ['.tar'],
            title: 'tkey;migrations.create.cisco.file_upload.cucm_configuration',
            body: 'tkey;migrations.create.cisco.file_upload.cucm_configuration.helptext',
            buttonLabel: 'tkey;migrations.create.cisco.file_upload.cucm_configuration.button',
            buttonIcon: ZiroIcons.IMPORT,
          }),
          hidden: () => this.formData.pbxType !== AssessmentPbxType.CISCO,
          valExcluded: () => this.formData.pbxType !== AssessmentPbxType.CISCO,
          required: true,
          validation: [
            {
              validator: (val: File[]) => {
                return from(
                  TarReader.load(val[0])
                    .then((tarFiles) => {
                      this._tarMissingCsvs = [];
                      MigrationsCreateFormComponent.REQUIRED_TAR_CSVS.forEach((requiredFile) => {
                        if (!tarFiles.fileInfos.some((item) => item.name.toLowerCase() === requiredFile)) {
                          this._tarMissingCsvs.push(requiredFile);
                        }
                      });
                      return this._tarMissingCsvs.length === 0;
                    })
                    .catch(() => false),
                ).pipe(
                  map((isValid) => {
                    return isValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                  }),
                );
              },
              message: () => {
                if (this._tarMissingCsvs.length === MigrationsCreateFormComponent.REQUIRED_TAR_CSVS.length) {
                  return 'tkey;migrations.create.cisco.file_upload.cucm_configuration.missing_all';
                }
                return {
                  content: 'tkey;migrations.create.cisco.file_upload.cucm_configuration.missing_csv',
                  params: { missingFiles: this._tarMissingCsvs.join(', ') },
                };
              },
            },
          ],
        },
        ciscoCdr: {
          dataAutomation: 'migrations-create-cisco-cdr-input',
          componentConfig: new FileUploadBoxConfig({
            maxSize: '2000MB',
            defaultFileTypes: ['.txt'],
            title: 'tkey;migrations.create.cisco.file_upload.cdr',
            body: 'tkey;migrations.create.cisco.file_upload.cdr.helptext',
            buttonLabel: 'tkey;migrations.create.cisco.file_upload.cdr.button',
            buttonIcon: ZiroIcons.IMPORT,
          }),
          hidden: () => this.formData.pbxType !== AssessmentPbxType.CISCO,
          valExcluded: () => this.formData.pbxType !== AssessmentPbxType.CISCO,
          validation: [
            {
              validator: (val: File[]) => {
                if (val && val[0]) {
                  return this._cdrValidationService
                    .validateCdrFile(val[0])
                    .pipe(
                      map((isValid) => (isValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID)),
                    );
                }
                return ZiroFormsValidationState.VALID;
              },
              message: 'tkey;migrations.create.cisco.file_upload.cdr.invalid_file',
            },
          ],
        },
        ciscoUnity: {
          dataAutomation: 'migrations-create-cisco-unity-input',
          componentConfig: new FileUploadBoxConfig({
            maxSize: '2000MB',
            defaultFileTypes: ['.zip'],
            title: 'tkey;migrations.create.cisco.file_upload.unity_configuration',
            body: 'tkey;migrations.create.cisco.file_upload.unity_configuration.helptext',
            buttonLabel: 'tkey;migrations.create.cisco.file_upload.unity_configuration.button',
            buttonIcon: ZiroIcons.IMPORT,
          }),
          hidden: () => this.formData.pbxType !== AssessmentPbxType.CISCO,
          valExcluded: () => this.formData.pbxType !== AssessmentPbxType.CISCO,
          validation: [
            {
              validator: (val: File[]) => {
                if (val && val[0]) {
                  return this._isUnityZipValid(val[0]).pipe(
                    map((isValid) => (isValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID)),
                  );
                }
                return ZiroFormsValidationState.VALID;
              },
              message: () => {
                let message = '';
                if (this._unityMissingFolders.length === 9) {
                  message = this._translateService.instant(
                    'tkey;migrations.create.cisco.file_upload.unity_configuration.missing_all',
                  );
                  return message;
                }
                if (this._unityMissingFolders.length) {
                  message += this._translateService.instant(
                    'tkey;migrations.create.cisco.file_upload.unity_configuration.missing_folder',
                    { missingFiles: this._unityMissingFolders.join(', ') },
                  );
                }
                if (this._unityMissingListJsons.length) {
                  if (message) {
                    message += '<br/>';
                  }
                  message += this._translateService.instant(
                    'tkey;migrations.create.cisco.file_upload.unity_configuration.missing_list_json',
                    { missingFiles: this._unityMissingListJsons.join(', ') },
                  );
                }
                return message;
              },
            },
          ],
        },
        ciscoCer: {
          dataAutomation: 'migrations-create-cisco-cer-input',
          componentConfig: new FileUploadBoxConfig({
            maxSize: '2000MB',
            defaultFileTypes: ['.csv'],
            multipleFiles: true,
            title: 'tkey;migrations.create.cisco.file_upload.cer_configuration',
            body: 'tkey;migrations.create.cisco.file_upload.cer_configuration.helptext',
            buttonLabel: 'tkey;migrations.create.cisco.file_upload.cer_configuration.button',
            buttonIcon: ZiroIcons.IMPORT,
          }),
          hidden: () => this.formData.pbxType !== AssessmentPbxType.CISCO,
          valExcluded: () => this.formData.pbxType !== AssessmentPbxType.CISCO,
          validation: [
            {
              validator: (val: File[]) => {
                if (val && val.length > 0) {
                  return this._isCerFileArrayValid(val).pipe(
                    map((isValid) => {
                      return isValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                    }),
                  );
                }
                return ZiroFormsValidationState.VALID;
              },
              message: () => {
                return this._cerValidationMessage;
              },
            },
          ],
        },
        ringCentralClientId: {
          label: 'tkey;migrations.create.ring_central.client_id',
          dataAutomation: 'migrations-create-ring-central-client-id-input',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          hidden: () => this.formData.pbxType !== AssessmentPbxType.RING_CENTRAL,
          valExcluded: () => this.formData.pbxType !== AssessmentPbxType.RING_CENTRAL,
        },
        ringCentralClientSecret: {
          label: 'tkey;migrations.create.ring_central.client_secret',
          dataAutomation: 'migrations-create-ring-central-client-secret-input',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          hidden: () => this.formData.pbxType !== AssessmentPbxType.RING_CENTRAL,
          valExcluded: () => this.formData.pbxType !== AssessmentPbxType.RING_CENTRAL,
        },
        ringCentralJwtToken: {
          label: 'tkey;migrations.create.ring_central.jwt_token',
          dataAutomation: 'migrations-create-ring-central-jwt-token-input',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          hidden: () => this.formData.pbxType !== AssessmentPbxType.RING_CENTRAL,
          valExcluded: () => this.formData.pbxType !== AssessmentPbxType.RING_CENTRAL,
        },
      },
    };
  }
}
